import { StaticImageData } from 'next/image'
import {
  ChangeEventHandler,
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Img } from '~/components/elements/Img'
import { SearchIcon } from '~/components/icons/generated/SearchIcon'
import { sendGTMEvent } from '~/features/shared/gtmEvent/sendGTMEvent'
import { GtmEvent } from '~/features/shared/gtmEvent/types/gtmEvent'
import notfound0 from '~/images/404/notfound_0.png'
import notfound4 from '~/images/404/notfound_4.png'
import notfoundBg from '~/images/404/notfound_bg.png'
import { Breadcrumb } from '../shared/breadcrumbList/components/Breadcrumb'
import { usePopularCoordinateTags } from '../shared/coordinate/hooks/usePopularCoordinateTags'
import { PopularCoordinates } from './components/PopularCoordinates'
import { PopularTags } from './components/PopularTags'
import { PopularUsers } from './components/PopularUsers'
import { usePopularCoordinates } from './lib/fetchPopularCoordinates'
import { usePopularUsers } from './lib/fetchPopularUsers'

type SwingImageProps = {
  src: StaticImageData
  width: number
  height: number
  alt: string
}

const SwingImage: React.FC<SwingImageProps> = ({ src, width, height, alt }) => {
  const [animationStarted, setAnimationStarted] = useState(false)

  const onMouseEnter = () => {
    if (animationStarted) return
    setAnimationStarted(true)
    setTimeout(() => setAnimationStarted(false), 500)
  }

  return (
    <p
      onMouseEnter={onMouseEnter}
      className={`origin-top ${animationStarted ? 'animate-swing404' : ''}`}
    >
      <Img src={src} width={width} height={height} alt={alt} />
    </p>
  )
}

export const Custom404: React.FC = () => {
  const [searchWord, setSearchWord] = useState<string>('')

  useEffect(() => {
    const path = `404.html?page=${document.location.pathname}${document.location.search}&from=${document.referrer}`
    const eventData: GtmEvent = {
      event: 'pageError',
      eventCategory: 'error',
      httpstatuscode: '404',
      fromPageGroup: path,
    }
    sendGTMEvent(eventData)
  }, [])

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setSearchWord(event.currentTarget.value)
    },
    [setSearchWord]
  )

  const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    (event) => {
      if (event.key === 'Enter') {
        if (event.nativeEvent.isComposing) return
        location.href = `https://wear.jp/coordinate/?search_word=${encodeURIComponent(
          searchWord
        )}`
        event.preventDefault()
      }
    },
    [searchWord]
  )

  const { data: coordinates } = usePopularCoordinates()
  const { data: members } = usePopularUsers()
  const { data: popularTags } = usePopularCoordinateTags({
    gender_key: 'WOMEN',
  })

  // TODO: dataLayerPushで404情報をGAに送る
  return (
    <div className="flex flex-col items-center justify-center gap-5 pt-[30px] xl:gap-[18px] xl:overflow-x-hidden xl:pt-[23px]">
      <div className="hidden pl-[5px] xl:block xl:w-[1002px] xl:items-start">
        <Breadcrumb />
      </div>
      <section className="hidden xl:flex xl:flex-col xl:items-center">
        <div className="pointer-events-none h-[108px] w-[3807px] xl:mt-[-64px]">
          <Img src={notfoundBg} alt="" />
        </div>
        <div className="relative top-[-9px] flex gap-[22px]">
          <SwingImage src={notfound4} width={70} height={141} alt="4" />
          <SwingImage src={notfound0} width={64} height={144} alt="0" />
          <SwingImage src={notfound4} width={70} height={141} alt="4" />
        </div>
      </section>

      <section className="flex flex-col items-center pb-5 xl:pb-[42px]">
        <h1 className="pb-4 text-[18px] font-bold xl:pb-[3px] xl:text-[24px]">
          ご指定のページが見つかりません
        </h1>
        <p className="text-center text-[12px] text-gray-600 xl:pb-2 xl:text-[15px]">
          お探しのページは見つかりません。
          <br />
          一時的にアクセスできない状態か、
          <br className="xl:hidden" />
          移動もしくは削除されてしまった可能性があります。
        </p>
        <div>
          <div className="relative my-5 flex-1">
            <SearchIcon className="absolute left-[14px] top-[10px] h-4 text-gray-600 xl:left-3 xl:top-[11px] xl:h-5" />
            <form action="#" className="mb-0">
              <input
                type="search"
                value={searchWord}
                onChange={onChange}
                onKeyDown={onKeyDown}
                placeholder="コーディネートを探す"
                className="w-[285px] rounded-[3px] border border-gray-300 px-[35px] py-[10px] text-center text-[13px] leading-none placeholder:text-black-400 xl:w-[310px] xl:py-3 xl:text-[15px] placeholder:xl:text-gray-600"
              />
            </form>
          </div>
          <div className="flex justify-center">
            <a
              href="/sitemap/"
              className="text-[13px] font-bold text-blue-400 xl:text-[14px] xl:hover:underline"
            >
              サイトマップを見る
            </a>
          </div>
        </div>
      </section>
      <section className="flex w-full flex-col items-center gap-4 xl:w-[1002px] xl:pb-5">
        {coordinates && (
          <div className="w-full p-[15px] xl:px-0">
            <PopularCoordinates
              coordinates={coordinates.coordinate_hourly_rankings}
            />
          </div>
        )}
        {members && (
          <div className="max-w-full pl-[15px] xl:pl-0">
            <PopularUsers members={members.official_recommended_members} />
          </div>
        )}
        {popularTags && (
          <div className="w-full px-[15px] pt-[10px] xl:px-0 xl:pt-[13px]">
            <PopularTags popularTags={popularTags.coordinate_tags} />
          </div>
        )}
      </section>
    </div>
  )
}
